import {
	alias,
	custom,
	deserialize,
	identifier,
	list,
	object,
	serializable,
} from "serializr";
import { IJsonObject } from "../api";
import { Attachment } from "../attachments";
import { Lead } from "../leads";

export enum ApplicationStatus {
	InProgress = "In Progress",
	Submitted = "Submitted",
	Review = "Review",
	Approved = "Approved",
	Rejected = "Rejected",
}

interface IParticipant {
	course?: string;
	numParticipants?: string;
	startDate?: Date;
}

interface IProps {
	id?: number | null;
	applicationId?: string;
	applyingAs?: string;
	preferredUccCampus?: string;
	studyPreference?: string;
	companyName?: string;
	companyPosition?: string;
	preferredLocation?: string;
	trainingLocationAddress?: string;
	trainingLocationCity?: string;
	trainingLocationCountry?: string;
	courseName?: string;
	studentStatus?: string;
	courseFeePaymentOption?: string;
	sponsorCompanyName?: string;
	sponsorAuthFullname?: string;
	sponsorAuthPosition?: string;
	sponsorAuthPhone?: string;
	sponsorAuthEmail?: string;
	sponsorLevel?: string;
	declarationFullname?: string;
	courseOther?: string;
	applyingFor?: string;
	sessionOption?: string;
	courseFeeHasMadePayment?: string;
	statusId?: number;
	currentStep?: number;
	lead?: Lead | null;
	participant?: Lead | null;
	applyingForA?: string;
	groupSize?: string;
	hasPaperBasedApplications?: string;
	requestingQuote?: boolean;
	participants?: IParticipant[];
	attachments?: Attachment[];
}

export class Application {
	public static fromJson(json: IJsonObject): Lead {
		return deserialize(Lead, json);
	}

	public static fromJsonArray(json: IJsonObject[]): Lead[] {
		return json.map((lead: IJsonObject) => this.fromJson(lead));
	}

	@serializable(identifier())
	public id: number | null;

	@serializable(alias("application_id"))
	public applicationId?: string;

	@serializable(alias("applying_as"))
	public applyingAs?: string;

	@serializable(alias("preferred_ucc_campus"))
	public preferredUccCampus?: string;

	@serializable(alias("study_preference"))
	public studyPreference?: string;

	@serializable(alias("company_name"))
	public companyName?: string;

	@serializable(alias("company_position"))
	public companyPosition?: string;

	@serializable(alias("preferred_location"))
	public preferredLocation?: string;

	@serializable(alias("training_location_address"))
	public trainingLocationAddress?: string;

	@serializable(alias("training_location_city"))
	public trainingLocationCity?: string;

	@serializable(alias("training_location_country"))
	public trainingLocationCountry?: string;

	@serializable(alias("course_name"))
	public courseName?: string;

	@serializable(alias("student_status"))
	public studentStatus?: string;

	@serializable(alias("course_fee_payment_option"))
	public courseFeePaymentOption?: string;

	@serializable(alias("sponsor_company_name"))
	public sponsorCompanyName?: string;

	@serializable(alias("session_option"))
	public sessionOption?: string;

	@serializable(alias("sponsor_auth_fullname"))
	public sponsorAuthFullname?: string;

	@serializable(alias("sponsor_auth_position"))
	public sponsorAuthPosition?: string;

	@serializable(alias("sponsor_auth_phone"))
	public sponsorAuthPhone?: string;

	@serializable(alias("sponsor_auth_email"))
	public sponsorAuthEmail?: string;

	@serializable(alias("course_other"))
	public courseOther?: string;

	@serializable(alias("sponsor_level"))
	public sponsorLevel?: string;

	@serializable(alias("declaration_fullname"))
	public declarationFullname?: string;

	@serializable(alias("applying_for"))
	public applyingFor?: string;

	@serializable(alias("course_fee_has_made_payment"))
	public courseFeeHasMadePayment?: string;

	@serializable(alias("current_step"))
	public currentStep: number;

	@serializable(alias("status_id"))
	public statusId: number;

	@serializable(object(Lead))
	public lead?: Lead | null;

	@serializable(object(Lead))
	public participant?: Lead | null;

	@serializable(alias("applying_for_a"))
	public applyingForA?: string;

	@serializable(alias("group_size"))
	public groupSize?: string;

	@serializable(alias("has_paper_based_applications"))
	public hasPaperBasedApplications?: string;

	@serializable(alias("requesting_quote"))
	public requestingQuote?: boolean;

	@serializable(custom(modelValue => modelValue, jsonValue => jsonValue))
	public participants?: IParticipant[];

	@serializable(list(object(Attachment)))
	public attachments?: Attachment[];

	constructor({
		id = null,
		applicationId = "",
		applyingAs = "",
		preferredUccCampus = "",
		studyPreference = "",
		courseName = "",
		studentStatus = "",
		courseFeePaymentOption = "",
		sponsorCompanyName = "",
		sponsorAuthFullname = "",
		sponsorAuthPosition = "",
		sponsorAuthPhone = "",
		sponsorAuthEmail = "",
		sponsorLevel = "",
		declarationFullname = "",
		applyingFor = "",
		companyName = "",
		companyPosition = "",
		preferredLocation = "",
		trainingLocationAddress = "",
		trainingLocationCity = "",
		trainingLocationCountry = "",
		sessionOption = "",
		courseFeeHasMadePayment = "",
		currentStep = 0,
		statusId = 0,
		lead = null,
		participant = null,
		applyingForA = "",
		groupSize = "",
		courseOther = "",
		hasPaperBasedApplications = "No",
		requestingQuote = false,
		participants = [],
		attachments = [],
	}: IProps = {}) {
		this.id = id;
		this.applicationId = applicationId;
		this.applyingAs = applyingAs;
		this.preferredUccCampus = preferredUccCampus;
		this.studyPreference = studyPreference;
		this.courseName = courseName;
		this.studentStatus = studentStatus;
		this.courseFeePaymentOption = courseFeePaymentOption;
		this.sponsorCompanyName = sponsorCompanyName;
		this.sponsorAuthFullname = sponsorAuthFullname;
		this.sponsorAuthPosition = sponsorAuthPosition;
		this.sponsorAuthPhone = sponsorAuthPhone;
		this.sponsorAuthEmail = sponsorAuthEmail;
		this.sponsorLevel = sponsorLevel;
		this.declarationFullname = declarationFullname;
		this.applyingFor = applyingFor;
		this.companyName = companyName;
		this.companyPosition = companyPosition;
		this.preferredLocation = preferredLocation;
		this.trainingLocationAddress = trainingLocationAddress;
		this.trainingLocationCity = trainingLocationCity;
		this.trainingLocationCountry = trainingLocationCountry;
		this.sessionOption = sessionOption;
		this.courseFeeHasMadePayment = courseFeeHasMadePayment;
		this.currentStep = currentStep;
		this.statusId = statusId;
		this.lead = lead;
		this.courseOther = courseOther;

		this.participant = participant;
		this.applyingForA = applyingForA;
		this.groupSize = groupSize;
		this.hasPaperBasedApplications = hasPaperBasedApplications;
		this.requestingQuote = requestingQuote;
		this.participants = participants;
		this.attachments = attachments;
	}

	public attachmentsForPrefix(prefix: string): Attachment[] {
		if (this.attachments) {
			const files = this.attachments.filter(u => {
				return u.filename.startsWith(prefix);
			});

			return files;
		}

		return [];
	}
}
